import { PatientType } from 'doctivity-shared/utils';
import { success } from 'redux-saga-requests';
import { API_TABLE } from 'store/actions/appActions';

import {
  APP_SELECT_CLIENT,
  APP_SET_DATE_RANGE,
  APP_SET_MARKET,
  APP_SET_SERVICELINE,
  APP_SET_PATIENT_TYPE,
  APP_SET_PROVIDERTYPE,
  APP_SET_REPORT,
  APP_SET_AI_QUERY,
  APP_SET_CHATBOT_STATE,
  SET_AI_LABEL_FILTER,
  SET_AI_REPORT,
  SET_AI_REPORT_PAGE_SIZE,
} from 'store/actions/appActions';
import {
  API_CLIENTS_LIST,
} from 'store/actions/clientsActions';
import {
  API_LOGOUT,
} from 'store/actions/loginActions';

// Defined outside the reducer so it is not recreated on every action
const AI_LABEL_FILTER_INITIAL = {
  open:          false,
  mode:          null,       // 'new_namespace' | 'new_tag' | 'existing_tag'
  namespaceName: null,       // max 20 chars — for new_namespace
  tagName:       null,       // for new_namespace and new_tag
  namespaceId:   null,       // for new_tag and existing_tag
  tagId:         null,       // for existing_tag
  initialPrompt: null,       // natural language description of providers
  criteria:      null,       // updated by chatbot refinements
  saveTrigger:   false,      // flipped to true to trigger save from chatbot
  savedResult:   null,       // { success, count, tag_id } after save
};

// AI Reporting — payload Key sends via launch_report and the report page renders.
const AI_REPORT_INITIAL = {
  open:          false,
  title:         null,
  reportType:    null,
  claimsType:    null,
  visualization: null,       // { type, x, y }
  spec:          null,       // normalized, client-scoped ReportSpec
  columns:       [],
  rows:          [],
  count:         0,
};

/**
 * Store global application state/settings here
 * @param {*} state
 * @param {*} action
 */
export const app = (state = {}, action = null) => {

  if (!state.patientType) {
    state.patientType = PatientType.ALL_PATIENTS;
  }

  // Initialize default chatbot state if missing
  if (!state.chatbot) {
    state.chatbot = {
      messages:  [],
      messageid: null,
      userid:    null,
    };
  }

  // Initialize aiLabelFilter at top level if missing
  if (!state.aiLabelFilter) {
    state.aiLabelFilter = AI_LABEL_FILTER_INITIAL;
  }

  // Initialize aiReport at top level if missing
  if (!state.aiReport) {
    state.aiReport = AI_REPORT_INITIAL;
  }

  switch (action.type) {
    case API_LOGOUT:
      return {};

    case APP_SET_CHATBOT_STATE:
      return {
        ...state,
        chatbot: {
          ...state.chatbot,
          ...(action.payload || {})
        }
      };

    case APP_SET_AI_QUERY:
      return { ...state, query: action.payload };

    case APP_SELECT_CLIENT: {
      const dateRange = state.dateRange;
      if (dateRange
        && dateRange.preset
        && dateRange.start
        && dateRange.end) {
        // this forces header to look at the NEW client end dates
        dateRange.start = dateRange.start.toString();
        dateRange.end = dateRange.end.toString();
      }
      return {
        ...state,
        selectedClient: action.payload,
        dateRange,
      };
    }

    case APP_SET_DATE_RANGE:
      return {
        ...state,
        dateRange: action.payload,
      };

    case APP_SET_SERVICELINE:
      return {
        ...state,
        serviceline: action.payload,
      };

    case APP_SET_PROVIDERTYPE:
      return {
        ...state,
        providerType: action.payload,
      };

    case APP_SET_REPORT:
      return {
        ...state,
        report: action.payload,
      };

    case APP_SET_MARKET:
      return {
        ...state,
        market: action.payload,
      };

    case success(API_CLIENTS_LIST): {
      const data = action.response?.data || action.data;

      let selectedClient = state.selectedClient;
      if (data && data.length > 0) {
        if (selectedClient) {
          const client = data.find((client) => client.id === selectedClient);
          if (!client) {
            // No access to previously selected client — default to first
            selectedClient = data[0].id;
          }
        } else {
          selectedClient = data[0].id;
        }
      }
      console.log('📊 Selected client:', selectedClient);
      return {
        ...state,
        selectedClient,
      };
    }

    case APP_SET_PATIENT_TYPE:
      return {
        ...state,
        patientType: action.payload,
      };

    case success(API_TABLE):
      return {
        ...state,
        querydata: action.response?.data || action.data,
      };

    case SET_AI_LABEL_FILTER:
      return {
        ...state,
        aiLabelFilter: {
          ...state.aiLabelFilter,
          ...action.payload,
        },
      };

    case SET_AI_REPORT:
      // launch_report sends a complete payload; replace over the initial defaults
      // so a fresh report never inherits stale rows/columns from a previous one.
      // Exception: keep the user's rows-per-page preference sticky across reports
      // (payload may still override it explicitly).
      return {
        ...state,
        aiReport: {
          ...AI_REPORT_INITIAL,
          pageSize: state.aiReport?.pageSize,
          ...action.payload,
        },
      };

    case SET_AI_REPORT_PAGE_SIZE:
      // Key-driven rows-per-page change — merge into the OPEN report, keep everything else.
      return {
        ...state,
        aiReport: {
          ...state.aiReport,
          pageSize: action.payload,
        },
      };

    default:
      return state;
  }
};